﻿import { FunctionComponent, h } from 'preact';
import { memo } from 'preact/compat';
import styles from './KPIVisualiser.module.scss';
import KpiVisualiserContext, { useLocalized } from './KPIVisualiser_Context';
import { dateObjToUtcDate, entryIndexToYPos, ENTRY_HEIGHT, ENTRY_MARGIN_TOP, ENTRY_PADDING, getKpiStateLabel, LEFT_HEADER_WIDTH } from './Utils';

interface Props {
    entry: KPIRuntime;
    index: Integer;
    isSelected: boolean;
}

export const KpiVisualiser_Entry_LeftHeader: FunctionComponent<Props> = memo(({ entry, index, isSelected }) => {
    const unknownKpiStr = useLocalized("lbl:unknownKpi", "-- Unknown KPI --");

    const onClick = () => {
        KpiVisualiserContext.pageReference?.invokeMethodAsync('JS_SelectKPI', entry.id);
    }

    const y = ENTRY_MARGIN_TOP + entryIndexToYPos(index);
    const box_top = y - ENTRY_PADDING;
    const box_bottom = y + ENTRY_HEIGHT + ENTRY_PADDING;
    const text_top = box_top + 6.0;
    const text_bottom = box_bottom - 6.0;

    const startDate = dateObjToUtcDate(entry.startDate);
    const endDate = entry.endDate ? dateObjToUtcDate(entry.endDate) : null;
    const deadlineDate = entry.targetDeadlineDate ? dateObjToUtcDate(entry.targetDeadlineDate) : new Date(startDate.getTime() + ((entry.kpiDefinition?.greenThreshold ?? 2) * 1000 * 60 * 60 * 24));
    const agreedDeadlineDate = entry.agreedDeadlineDate ? dateObjToUtcDate(entry.agreedDeadlineDate) : null;

    const displayNameLabel = `${entry.kpiDefinition?.displayName ?? unknownKpiStr}`;
    const stateLabel = getKpiStateLabel(startDate, agreedDeadlineDate ?? deadlineDate, endDate, entry.isPrediction);
    const timespanLabel = `${startDate.toLocaleDateString()} - ${(endDate ?? agreedDeadlineDate ?? deadlineDate).toLocaleDateString()}`;
    const title = `${displayNameLabel} (${entry.kpiDefinition?.internalName ?? unknownKpiStr}) at action ${entry.action}\n${stateLabel}\n${timespanLabel}`;

    let actionLabel;
    if (entry.endAction && entry.action != entry.endAction) {
        actionLabel = `${entry.action}-${entry.endAction}`;
    } else {
        actionLabel = entry.endAction ?? entry.action;
    }

    let displayNameLabelWidth = 0;
    const textLengthMeasurerSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    const textLengthMeasurer = document.createElementNS("http://www.w3.org/2000/svg", "text");
    if (textLengthMeasurerSvg && textLengthMeasurer && textLengthMeasurer.getComputedTextLength) {
        textLengthMeasurer.textContent = displayNameLabel;
        textLengthMeasurer.setAttribute("font-weight", "bold");
        document.body.appendChild(textLengthMeasurerSvg);
        textLengthMeasurerSvg.appendChild(textLengthMeasurer);
        displayNameLabelWidth = textLengthMeasurer.getComputedTextLength();
        textLengthMeasurerSvg.remove();
    }

    return (
        <g class={`${styles.kpiEntry_leftHeader} ${isSelected ? styles.selected : ""}`} onClick={onClick}>
            <title>{title}</title>

            <rect x={0} y={box_top} width={LEFT_HEADER_WIDTH} height={ENTRY_HEIGHT + ENTRY_PADDING * 2} class={styles.containerBox} />

            <line x1={0} y1={box_top} x2={LEFT_HEADER_WIDTH} y2={box_top} stroke="rgba(0,0,0,0.2)" stroke-width={2} />
            <line x1={0} y1={box_bottom} x2={LEFT_HEADER_WIDTH} y2={box_bottom} stroke="rgba(0,0,0,0.2)" stroke-width={2} />
            <line x1={LEFT_HEADER_WIDTH} y1={box_top} x2={LEFT_HEADER_WIDTH} y2={box_bottom} stroke="rgba(0,0,0,0.2)" stroke-width={2} />

            <text x={8} y={text_top} text-anchor="start" dominant-baseline="hanging" textLength={Math.min(displayNameLabelWidth, 208)} lengthAdjust="spacingAndGlyphs" font-weight="bold">
                {displayNameLabel}
            </text>

            <text x={8} y={text_top + (text_bottom - text_top) / 2.0} text-anchor="start" dominant-baseline="central" font-size="0.9em">
                {stateLabel}
            </text>

            <text x={8} y={text_bottom - 4.0} text-anchor="start" dominant-baseline="bottom" font-size="0.9em">
                {timespanLabel}
            </text>

            <ActionWidget centerX={LEFT_HEADER_WIDTH - 20} centerY={text_top + 8} label={actionLabel} />
        </g>
    );
});

interface ActionWidgetProps {
    label: string;
    centerX: number;
    centerY: number;
}

const ActionWidget: FunctionComponent<ActionWidgetProps> = memo(({ label, centerX, centerY }) => {
    const width = 32.0;
    const height = 18.0;

    return (
        <g transform={`translate(${centerX - width / 2.0}, ${centerY - height / 2.0})`} class={styles.actionWidget}>
            <rect x={0} y={0} width={width} height={height} rx={8} ry={12} />
            <text x={width / 2.0} y={3} font-size="0.75em" font-weight="bold" text-anchor="middle" dominant-baseline="hanging">
                {label}
            </text>
        </g>
    );
});